import {
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	Theme,
	Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SelectGroup } from 'components/Form/SelectGroup/SelectGroup';
import { SelectWrapper } from 'components/Form/SelectGroup/SelectWrapper';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import DateSelect from '../../../components/Form/DateSelect/DateSelect';
import NavigationBar from '../../../components/NavigationBar';
import { PageTitle } from '../../../components/PageTitle/PageTitle';
import { useUniversalTracking } from '../../../lib/tracking/universalTracking';
import { getDateComponent } from '../../../lib/utils';
import { colors } from '../../../theme/theme';
import { AppState, ContractState, FahrzeugType } from '../../../types/redux';
import { toEnum } from '../../../utils/enum';
import { formatVehicel } from "../../../utils/formatString";

interface DateParts {
	day?: string;
	month?: string;
	year?: string;
}

interface KppVehicleProps extends StateProps, DispatchProps {
	onSubmit(data: any): void;
}

enum CarState {
	NEW_CAR = 'newCar',
	USED_CAR = 'usedCar',
}

const useStyles = makeStyles((theme: Theme) => ({
	subtitle: {
		marginBottom: theme.spacing(4),
	},
	sectionTitle: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(2),
	},
	errorMessage: {
		color: colors.red,
		fontSize: 14,
		marginLeft: 10,
		marginTop: theme.spacing(1),
	},
}));

const InternalKppVehicle: React.FC<KppVehicleProps> = (props) => {
	const { t } = useTranslation();
	const { trackEvent } = useUniversalTracking();
	const classes = useStyles();

	const [carState, setCarState] = React.useState<CarState>(
		props.contract.neufahrzeug ? CarState.NEW_CAR : CarState.USED_CAR
	);

	const [carTyp, setCarTyp] = React.useState<FahrzeugType>( FahrzeugType.P );
	
	const initialBuyDate =
		props.contract.kaufdatum || moment().format('YYYY-MM-DD');

	const [buyYear, setBuyYear] = useState(
		getDateComponent(initialBuyDate, 'year')
	);
	const [buyMonth, setBuyMonth] = useState(
		getDateComponent(initialBuyDate, 'month')
	);
	const [buyDay, setBuyDay] = useState(getDateComponent(initialBuyDate, 'day'));
	const [buyDateError, setBuyDateError] = useState('');

	const handleChangeIsNewCar = (event: React.ChangeEvent<HTMLInputElement>) => {
		const carStateEnum: CarState = toEnum(CarState, (event.target as HTMLInputElement).value as CarState) || CarState.NEW_CAR;

		setCarState(carStateEnum);
		validateBuyDate({}, carStateEnum);
	};

	const handleChangeCarTyp = (event: React.ChangeEvent<HTMLInputElement>) => {
		const carTypEnum: FahrzeugType = toEnum(FahrzeugType, (event.target as HTMLInputElement).value as FahrzeugType) || FahrzeugType.P;
		// console.log(" what is it : " + carTypEnum)
		setCarTyp(carTypEnum);
		// validateBuyDate({}, );
	};
	
	const validateBuyDate = (
		date: DateParts = {},
		newCarState?: CarState
	): boolean => {
		const dateString = `${date.year || buyYear}-${date.month || buyMonth}-${
			date.day || buyDay
		}`;
		const buyDate = moment(dateString);
		const nowDate = moment();
		const cs = newCarState ?? carState;

		let error = '';
		if (!buyDate.isValid()) {
			// invalid date
			error = t('kppVehicle.errors.invalidDate');
		} else if (
			cs === CarState.NEW_CAR &&
			nowDate.subtract(1, 'years').diff(buyDate, 'days') > 0
		) {
			// can't begin insurance 12 month after buy date of new car
			error = t('kppVehicle.errors.buyDatePastNewCar');
		} else if (
			cs === CarState.USED_CAR &&
			nowDate.subtract(6, 'months').diff(buyDate, 'days') > 0
		) {
			// can't begin insurance 6 month after buy date of old car
			error = t('kppVehicle.errors.buyDatePastUsedCar');
		}

		setBuyDateError(error);

		return error === '';
	};

	const formIsValid = () =>
		carState !== null &&
		!!buyDay &&
		!!buyMonth &&
		!!buyYear &&
		validateBuyDate();

	const validateAndSubmit = () => {
		if (formIsValid()) {
			onSubmit();
		}
	};

	const onSubmit = () => {
		const data = {
			fahrzeugtyp: carTyp,
			neufahrzeug: carState === CarState.NEW_CAR,
			kaufdatum: `${buyYear}-${buyMonth}-${buyDay}`,
		};
		trackEvent({
			category: 'fahrzeug-submit',
			action: 'click-event',
			label: 'fahrzeug',
		});
		props.onSubmit(data);
	};

	return (
		<div className="kpp_vehicle_main">
			<PageTitle title={t('kppVehicle.title')} marginBottom={1} />
			<Typography
				variant="body1"
				color="textSecondary"
				className={classes.subtitle}
			>
				{/*{t('kppVehicle.subtitle')}*/}
			</Typography>
			<Typography variant="h3" className={classes.sectionTitle}>
				{t('kppVehicle.section_title')}
			</Typography>
			<FormControl component="fieldset">
				<RadioGroup
					aria-label="Fahrzeugtyp"
					className="kppCarState"
					name="gender1"
					value={carTyp}
					onChange={handleChangeCarTyp}
				>
					<FormControlLabel
						value={FahrzeugType.P}
						control={<Radio color="primary" />}
						label={formatVehicel(FahrzeugType.P)}
					/>
					<FormControlLabel
						value={FahrzeugType.M}
						control={<Radio color="primary" />}
						label={formatVehicel(FahrzeugType.M)}
					/>
					<FormControlLabel
						value={FahrzeugType.W	}
						control={<Radio color="primary" />}
						label={formatVehicel(FahrzeugType.W)}
					/>
				</RadioGroup>
			</FormControl>
			<Typography variant="h3" className={classes.sectionTitle}>
				{t('kppVehicle.section1_title')}
			</Typography>
			<FormControl component="fieldset">
				<RadioGroup
					aria-label="Fahrzeugzustand"
					className="kppCarState"
					name="gender1"
					value={carState}
					onChange={handleChangeIsNewCar}
				>
					<FormControlLabel
						value={CarState.NEW_CAR}
						control={<Radio color="primary" />}
						label="Neu"
					/>
					<FormControlLabel
						value={CarState.USED_CAR}
						control={<Radio color="primary" />}
						label="Gebraucht"
					/>
				</RadioGroup>
			</FormControl>
			<Typography
				variant="h3"
				className={`${classes.sectionTitle} kppBuyDateTitle`}
			>
				{t('kppVehicle.section2_title')}
			</Typography>
			<SelectGroup>
				<SelectWrapper>
					<DateSelect
						selectedValue={buyDay}
						setSelectedValue={(value) => {
							setBuyDay(value);
							validateBuyDate({ day: value });
						}}
						placeholder={t('common.date.day')}
						error=""
						dataCy="buy_day"
						dateType="day"
						defaultDate={moment().toDate()}
					/>
				</SelectWrapper>
				<SelectWrapper>
					<DateSelect
						selectedValue={buyMonth}
						setSelectedValue={(value) => {
							setBuyMonth(value);
							validateBuyDate({ month: value });
						}}
						placeholder={t('common.date.month')}
						error=""
						dataCy="buy_month"
						dateType="month"
						defaultDate={moment().toDate()}
					/>
				</SelectWrapper>
				<SelectWrapper>
					<DateSelect
						selectedValue={buyYear}
						setSelectedValue={(value) => {
							setBuyYear(value);
							validateBuyDate({ year: value });
						}}
						placeholder={t('common.date.year')}
						error=""
						dataCy="buy_year"
						dateType="year"
						defaultDate={moment().add(1, 'years').toDate()}
					/>
				</SelectWrapper>
			</SelectGroup>
			{!!buyDateError && (
				<div className={classes.errorMessage}>{buyDateError}</div>
			)}
			<NavigationBar onClickContinue={validateAndSubmit} />
		</div>
	);
};

interface StateProps {
	contract: ContractState;
}

interface DispatchProps {}

const mapStateToProps = (state: AppState) => ({
	contract: state.contract,
});

/* istanbul ignore next */
const mapDispatchToProps = (
	dispatch: ThunkDispatch<Promise<any>, void, Action>
) => ({});

export const KppVehicle = connect(
	mapStateToProps,
	mapDispatchToProps
)(InternalKppVehicle);
